import type { AIWorkerRequest, AIWorkerResponse } from './ai-worker-protocol.js';
import AIWorker from './ai-decision.worker.ts?worker&inline';

export interface AIWorkerTransport {
  postMessage(message: AIWorkerRequest): void;
  subscribe(handler: (message: AIWorkerResponse) => void): () => void;
  terminate(): void;
}

export interface MockAIWorkerTransportOptions {
  onDecide?: (request: Extract<AIWorkerRequest, { type: 'DECIDE' }>) => AIWorkerResponse | undefined;
  onAdvise?: (request: Extract<AIWorkerRequest, { type: 'ADVISE' }>) => AIWorkerResponse | undefined;
}

export function createMockAIWorkerTransport(
  options: MockAIWorkerTransportOptions = {},
): AIWorkerTransport {
  const listeners = new Set<(message: AIWorkerResponse) => void>();
  const cancelled = new Set<string>();

  return {
    postMessage(message) {
      if (message.type === 'CANCEL') {
        cancelled.add(message.requestId);
        return;
      }

      if (cancelled.has(message.requestId)) {
        cancelled.delete(message.requestId);
        return;
      }

      const response =
        message.type === 'DECIDE'
          ? options.onDecide?.(message)
          : options.onAdvise?.(message);
      if (!response) return;

      queueMicrotask(() => {
        if (cancelled.has(message.requestId)) {
          cancelled.delete(message.requestId);
          return;
        }
        for (const listener of listeners) {
          listener(response);
        }
      });
    },
    subscribe(handler) {
      listeners.add(handler);
      return () => {
        listeners.delete(handler);
      };
    },
    terminate() {
      listeners.clear();
      cancelled.clear();
    },
  };
}

export function createBrowserAIWorkerTransport(): AIWorkerTransport | null {
  if (typeof Worker === 'undefined') {
    return null;
  }

  try {
    const worker = new AIWorker({ name: 'texas42-ai' });

    const listeners = new Set<(message: AIWorkerResponse) => void>();
    worker.addEventListener('message', (event: MessageEvent<AIWorkerResponse>) => {
      for (const listener of listeners) {
        listener(event.data);
      }
    });

    return {
      postMessage(message) {
        worker.postMessage(message);
      },
      subscribe(handler) {
        listeners.add(handler);
        return () => {
          listeners.delete(handler);
        };
      },
      terminate() {
        listeners.clear();
        worker.terminate();
      },
    };
  } catch {
    return null;
  }
}
